
import { ElForm, ElMessage, ElMessageBox } from 'element-plus'
import { defineComponent, reactive, toRefs, onMounted, ref } from 'vue'
import Container from '@/components/form/container/Container.vue'
import SearchForm from '@/components/form/search/SearchForm.vue'
import SimpleTable from '@/components/table/simple/SimpleTable.vue'
import CenterForm from '@/components/form/common/CenterForm.vue'
import NomalContainer from '@/components/dfms-components/container/NomalContainer.vue'
import {
  MaterialAttributeDefModel,
  MaterialAttributeDefPageSearchModel,
  MaterialAttributeDefSearchModel,
  MaterialAttributeDefAddModel,
  MaterialAttributeDefUpdateModel,
  IdsModel
} from '@/model/product/MaterialAttributeDefModel'
import {
  pageSearchMaterialAttributeDef,
  addMaterialAttributeDef,
  updateMaterialAttributeDef,
  removeMaterialAttributeDef,
  batchRemoveMaterialAttributeDef
} from '@/apis/product/materialAttributeDef'
import { parseTime } from '@/utils'
import { createModel } from '@/utils/component'
import { selectOptionFormat } from '@/utils/options'
import { ValueType } from '@/constant/options'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  components: {
    Container,
    SearchForm,
    SimpleTable,
    CenterForm,
    NomalContainer
  },
  setup() {
    const { t } = useI18n()
    const searchFormRef = ref(ElForm)
    const valueTypeOption = selectOptionFormat(ValueType.valueTypeOptions, t, ValueType.i18nKey)
    const searchStruct = [
      { prop: 'name', label: t('materialAttributeDef.name'), placeholder: t('materialAttributeDef.nameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'valueType', label: t('materialAttributeDef.valueType'), placeholder: t('materialAttributeDef.valueTypeP'), component: 'el-select', type: 'number', model: null, options: valueTypeOption }
    ]

    const createStruct = [
      { prop: 'name', label: t('materialAttributeDef.name'), placeholder: t('materialAttributeDef.nameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'valueType', label: t('materialAttributeDef.valueType'), placeholder: t('materialAttributeDef.valueTypeP'), component: 'el-select', type: 'number', model: null, options: valueTypeOption }
    ]

    const updateStruct = [
      { prop: 'name', label: t('materialAttributeDef.name'), placeholder: t('materialAttributeDef.nameP'), component: 'el-input', type: 'text', model: null },
      { prop: 'valueType', label: t('materialAttributeDef.valueType'), placeholder: t('materialAttributeDef.valueTypeP'), component: 'el-select', type: 'number', model: null, options: valueTypeOption }
    ]

    const materialAttributeDefRole = {
      name: [{ required: true, message: t('materialAttributeDef.name').concat(t('common.isRequired')), trigger: 'blur' }],
      valueType: [{ required: true, message: t('materialAttributeDef.valueType').concat(t('common.isRequired')), trigger: 'blur' }]
    }

    const tableStruct = [

      { prop: 'name', label: t('materialAttributeDef.name'), sortable: 'custom' },
      { prop: 'valueType', label: t('materialAttributeDef.valueType'), sortable: 'custom' },
      { prop: 'createTime', label: t('common.createTime'), formatter: (row: MaterialAttributeDefModel) => { return parseTime(row.createTime) }, sortable: 'custom' },
      { prop: 'updateTime', label: t('common.updateTime'), formatter: (row: MaterialAttributeDefModel) => { return parseTime(row.updateTime) }, sortable: 'custom' }
    ]
    const dataMap = reactive({
      searchFormStruct: searchStruct,
      searchFormModel: createModel(searchStruct) as MaterialAttributeDefSearchModel,
      tableData: Array<MaterialAttributeDefModel>(),
      columnStruct: tableStruct,
      listLoading: true,
      selection: Array<number>(),
      limit: 10,
      total: 0,
      page: 1,
      sort: 'createTime',
      order: 'desc',
      dialogStatus: 'create',
      textMap: {
        update: 'materialAttributeDef.edit',
        create: 'materialAttributeDef.create'
      },
      dialogFormVisible: false,
      materialAttributeDefRole: materialAttributeDefRole,
      createStruct: createStruct,
      createModel: createModel(createStruct) as MaterialAttributeDefAddModel,
      updateStruct: updateStruct,
      updateModel: createModel(updateStruct) as MaterialAttributeDefUpdateModel,
      editRow: {} as MaterialAttributeDefModel,
      handleSelection(item: any[]) {
        dataMap.selection = item
      },
      handleCurrentChange(page?: any) {
        dataMap.page = page
        dataMap.getList()
      },
      handleSizeChange(val: any) {
        dataMap.limit = val
        dataMap.getList()
      },
      handleOrder(column: any) {
        dataMap.sort = column.prop
        dataMap.order = column.order === 'descending' ? 'desc' : 'asc'
        dataMap.getList()
      },
      async getList() {
        dataMap.listLoading = true
        const params: MaterialAttributeDefPageSearchModel = {
          name: dataMap.searchFormModel.name,
          valueType: dataMap.searchFormModel.valueType,
          page: dataMap.page,
          limit: dataMap.limit,
          sort: dataMap.sort,
          order: dataMap.order
        }
        const data = await pageSearchMaterialAttributeDef(params)
        dataMap.tableData = data?.data?.records ?? []
        const total = data?.data?.total ?? '0'
        dataMap.total = parseInt(total)

        // Just to simulate the time of the request
        setTimeout(() => {
          dataMap.listLoading = false
        }, 0.5 * 1000)
      },
      submitSearch() {
        dataMap.page = 1
        dataMap.getList()
      },
      resetForm() {
        console.log('reset')
      },
      async createSubmit() {
        const createData: MaterialAttributeDefAddModel = {
          name: dataMap.createModel.name,
          valueType: dataMap.createModel.valueType
        }
        const result = await addMaterialAttributeDef(createData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.createSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async updateNameSubmit() {
        const updateData: MaterialAttributeDefUpdateModel = {
          id: dataMap.updateModel.id,
          name: dataMap.updateModel.name,
          valueType: dataMap.updateModel.valueType
        }
        const result = await updateMaterialAttributeDef(updateData)
        dataMap.dialogFormVisible = false
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.updateSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async deleteData(id: number) {
        const result = await removeMaterialAttributeDef(id)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      async bathDeleteData() {
        const deleteData: IdsModel = { ids: dataMap.selection }
        const result = await batchRemoveMaterialAttributeDef(deleteData)
        dataMap.getList()
        if (result?.code === 0) {
          ElMessage({
            message: t('common.deleteSuccess'),
            type: 'success',
            duration: 2000
          })
        } else {
          ElMessage({
            message: result?.message,
            type: 'error',
            duration: 2000
          })
        }
      },
      updateNameReset() {
        console.log('update reset')
        dataMap.updateModel.id = dataMap.editRow.id
        dataMap.updateModel.name = dataMap.editRow.name
        dataMap.updateModel.valueType = dataMap.editRow.valueType
      },
      createReset() {
        console.log('create reset')
      }
    })
    const handleCreate = () => {
      dataMap.dialogStatus = 'create'
      dataMap.dialogFormVisible = true
    }
    const handleEdit = (row?: MaterialAttributeDefModel) => {
      if (row) {
        dataMap.editRow = row
        dataMap.dialogStatus = 'update'
        dataMap.updateModel.id = row.id
        dataMap.updateModel.name = row.name
        dataMap.updateModel.valueType = row.valueType
        dataMap.dialogFormVisible = true
      }
    }
    const handleDelete = (id: number) => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.deleteData(id)
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    const handleBathDelete = () => {
      ElMessageBox.confirm(t('common.deleteTip'), t('message.tip'), {
        confirmButtonText: t('message.confirm'),
        cancelButtonText: t('message.cancel'),
        type: 'warning'
      }).then(async() => {
        await dataMap.bathDeleteData()
      }).catch(() => {
        ElMessage({
          type: 'info',
          message: t('common.cancelDelete'),
          duration: 2000
        })
      })
    }
    onMounted(() => {
      dataMap.getList()
    })
    return {
      t,
      handleCreate,
      handleEdit,
      handleDelete,
      handleBathDelete,
      searchFormRef,
      ...toRefs(dataMap)
    }
  }
})
